import { useSearchParams } from "react-router-dom";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import {
  NotificationPreferencesSection,
  PartyLookupCard,
} from "@/features/notification/components";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";

/**
 * Préférences par canal (SMS/EMAIL/PUSH) d'une identité
 * (`GET /NotificationPreferences/party/{partyId}` + `PUT .../party/{partyId}/{channel}`).
 *
 * Le `partyId` est piloté par le query param `?partyId=...` — l'écran offre
 * aussi un lookup card qui met à jour le paramètre d'URL (persistant, partageable).
 * Tant qu'aucun partyId n'est fourni, on affiche un état vide.
 *
 * Rôles page : flashpay-admin, flashpay-superadmin (back-office édite les
 * préférences d'un client ; le service back n'impose pas de rôle mais le
 * gateway exige une session authentifiée).
 */
export function NotificationPreferencesPage() {
  const { t } = useNotificationsTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const partyId = searchParams.get("partyId") ?? undefined;

  const onSelect = (nextPartyId: string) => {
    // Persist dans l'URL pour partage / rafraîchissement — API idiomatique
    // react-router v7 (Set garantit la mutation immuable côté hook).
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("partyId", nextPartyId);
      return next;
    });
  };

  return (
    <RequireRole
      roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
    >
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-foreground">
            {t("notifications.preferences.title")}
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t("notifications.preferences.subtitle")}
          </p>
        </div>

        <PartyLookupCard partyId={partyId} onSelect={onSelect} />

        {partyId ? (
          <NotificationPreferencesSection partyId={partyId} />
        ) : (
          <p className="rounded-md border border-dashed border-border bg-muted/40 p-6 text-sm text-muted-foreground">
            {t("notifications.preferences.emptyState")}
          </p>
        )}
      </section>
    </RequireRole>
  );
}
